import Logger from '../lib/logger';
import { belowLaptopMediaQuery } from '../lib/breakpoints';
import KeenSlider from 'keen-slider';

export default class ProfessionalImageCarousel {
	constructor(dom) {
		this.NAME = 'ProfessionalImageCarousel';

		this.dom = dom;
		this.slidesContainerDom = dom.querySelector('[data-professional-image-carousel-slides]');
		this.dotsContainerDom = dom.querySelector('[data-professional-image-carousel-dots]');
		this.previousButtonDom = dom.querySelector('[data-professional-image-carousel-previous]');
		this.nextButtonDom = dom.querySelector('[data-professional-image-carousel-next]');

		this.slider = null;
		this.intersectionObserver = null;
	}

	init() {
		Logger.info(`${this.NAME} > init`);

		if (!this.slidesContainerDom) return;

		const slides = this.slidesContainerDom.querySelectorAll('.professional-image-carousel__slide');
		if (!slides.length) return;

		this.previousButtonDom?.addEventListener('click', () => this.slider?.prev());
		this.nextButtonDom?.addEventListener('click', () => this.slider?.next());

		const self = this;

		this.intersectionObserver = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					self.initializeCarousel();
				}
			});
		});

		this.intersectionObserver.observe(this.dom);
	}

	initializeCarousel() {
		Logger.info(`${this.NAME} > initializeCarousel`);

		if (this.slider) return;

		const desktopCols = Math.max(
			1,
			parseInt(this.dom.getAttribute('data-columns-desktop'), 10) || 3
		);
		const mobileCols = Math.max(
			1,
			parseInt(this.dom.getAttribute('data-columns-mobile'), 10) || 1
		);
		const self = this;

		this.slider = new KeenSlider(
			this.slidesContainerDom,
			{
				selector: '.professional-image-carousel__slide',
				loop: false,
				slides: {
					perView: desktopCols,
					spacing: 16,
				},
				breakpoints: {
					[`(${belowLaptopMediaQuery})`]: {
						slides: {
							perView: mobileCols === 1 ? 1.15 : mobileCols,
							spacing: 8,
						},
					},
				},
				created(slider) {
					Logger.info(`ProfessionalImageCarousel > created`);
					if (self.intersectionObserver) {
						self.intersectionObserver.disconnect();
						self.intersectionObserver = null;
					}
					self.slider = slider;
					self.buildDots();
					self.updateDots();
					self.updateArrows();
				},
				slideChanged(slider) {
					self.slider = slider;
					self.updateDots();
					self.updateArrows();
				},
				optionsChanged(slider) {
					self.slider = slider;
					self.buildDots();
					self.updateDots();
					self.updateArrows();
				},
			}
		);
	}

	buildDots() {
		if (!this.dotsContainerDom || !this.slider) return;

		const details = this.slider.track.details;
		if (!details) return;

		const dotCount = (details.maxIdx != null ? details.maxIdx : details.slides.length - 1) + 1;

		this.dotsContainerDom.innerHTML = '';
		if (dotCount <= 1) return;

		for (let i = 0; i < dotCount; i++) {
			const dot = document.createElement('button');
			dot.type = 'button';
			dot.className = 'professional-image-carousel__dot';
			dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
			dot.addEventListener('click', () => {
				this.slider.moveToIdx(i);
			});
			this.dotsContainerDom.appendChild(dot);
		}
	}

	updateDots() {
		if (!this.dotsContainerDom || !this.slider) return;

		const details = this.slider.track.details;
		if (!details) return;

		const activeIdx = details.rel;
		const dots = this.dotsContainerDom.querySelectorAll('.professional-image-carousel__dot');
		dots.forEach((dot, i) => {
			dot.classList.toggle('professional-image-carousel__dot--active', i === activeIdx);
		});
	}

	updateArrows() {
		if (!this.previousButtonDom || !this.nextButtonDom || !this.slider) return;

		const details = this.slider.track.details;
		if (!details) return;

		const hasMultiplePages = details.maxIdx > details.minIdx;
		this.previousButtonDom.hidden = !hasMultiplePages;
		this.nextButtonDom.hidden = !hasMultiplePages;
		this.previousButtonDom.disabled = details.rel <= details.minIdx;
		this.nextButtonDom.disabled = details.rel >= details.maxIdx;
	}
}
